import { defineComponent as _defineComponent } from 'vue'
import type { BookV1 } from '@ilteducation/content-registry-types';
import { computed, ref } from 'vue';
import DuplicateBooksTable from '@/components/book/DuplicateBooksTable.vue';
import { useLanguageLabel } from '@/comp/use-ref-languages';
import { copyAudioFiles, waitForBookToHaveAudio } from '@/data-provider/books';

type Props = {
  item: BookV1;
};

type AudioLanguage = {
  name: string;
  code: string;
};

type CopyStatus = 'DONE' | 'ERROR' | 'IN_PROGRESS';


export default /*#__PURE__*/_defineComponent({
  __name: 'BookAudioCopyPaste',
  props: {
    item: null
  },
  setup(__props: any) {

const props = __props as {
  item: BookV1;
}

const audioLanguagesTableHeaders = [
  { text: 'Language', value: 'name' },
  { text: 'Language Code', value: 'code' },
];


const bulkUpdateDialog = ref(false);
const isCopyingStep = ref(false);
const selectedAudioLanguages = ref<AudioLanguage[]>([]);
const selectedDuplicateBooks = ref<BookV1[]>([]);

const completedMigrations = ref<{ key: string; status: CopyStatus }[]>([]);

const handleSelectedDuplicateBooks = (selectedBooks: BookV1[]) => {
  selectedDuplicateBooks.value = selectedBooks;
};

const toggleDialog = () => {
  bulkUpdateDialog.value = !bulkUpdateDialog.value;
};

const { getLanguageLabel } = useLanguageLabel();

const audioLanguages = computed<AudioLanguage[]>(() =>
  (props.item.languages.audio || []).map((languageCode) => ({
    name: getLanguageLabel.value(languageCode),
    code: languageCode,
  })),
);

const getUpdateKey = (targetBook: BookV1, languageCode: string) => `${targetBook.id}-${languageCode}`;

const setCopyState = (targetBook: BookV1, languageCode: string, status: CopyStatus) => {
  completedMigrations.value = [...completedMigrations.value, { key: getUpdateKey(targetBook, languageCode), status }];
};

const copyLanguagesForBookByLanguage = async (targetBook: BookV1, language: AudioLanguage) => {
  await copyAudioFiles(props.item, targetBook, language.code);
  await waitForBookToHaveAudio(targetBook.id, language.code);
  setCopyState(targetBook, language.code, 'DONE');
};

const copyLanguagesForBook = async (targetBook: BookV1) => {
  // We update the languages one by one because we don't support uploading files for multiple languages, same book in parallel
  for (let languageIndex = 0; languageIndex < selectedAudioLanguages.value.length; languageIndex++) {
    const selectedLanguage = selectedAudioLanguages.value[languageIndex];

    try {
      // eslint-disable-next-line no-await-in-loop
      await copyLanguagesForBookByLanguage(targetBook, selectedLanguage);
    } catch (error) {
      for (
        let failedLanguagesIndex = languageIndex;
        failedLanguagesIndex < selectedAudioLanguages.value.length;
        failedLanguagesIndex++
      ) {
        const failedLanguage = selectedAudioLanguages.value[failedLanguagesIndex];
        setCopyState(targetBook, failedLanguage.code, 'ERROR');
      }

      // Do not attempt further languages for this book if one failed
      break;
    }
  }
};

const copyLanguages = async () => {
  completedMigrations.value = [];

  await Promise.all(selectedDuplicateBooks.value.map(async (book) => copyLanguagesForBook(book)));
};

const handleSubmit = async () => {
  const confirmMessage = `Are you sure you want to copy audio files to the selected books?`;
  // eslint-disable-next-line no-restricted-globals
  if (!confirm(confirmMessage)) {
    return;
  }

  isCopyingStep.value = true;

  await copyLanguages();
};

const getCopyStatus = computed(() => (targetBook: BookV1, languageCode: string) => {
  const updateKey = getUpdateKey(targetBook, languageCode);
  const migration = completedMigrations.value.find((m) => m.key === updateKey);

  return migration?.status || 'IN_PROGRESS';
});

const isCopyInProgress = computed(() => (targetBook: BookV1, languageCode: string) => {
  const updateKey = getUpdateKey(targetBook, languageCode);

  return !completedMigrations.value.find((m) => m.key === updateKey);
});

const isCopyDone = computed(() => (targetBook: BookV1, languageCode: string) => {
  const status = getCopyStatus.value(targetBook, languageCode);

  return status === 'DONE';
});

const hasCopyError = computed(() => (targetBook: BookV1, languageCode: string) => {
  const status = getCopyStatus.value(targetBook, languageCode);
  return status === 'ERROR';
});

const areAllCopiesDone = computed(
  () => completedMigrations.value.length === selectedDuplicateBooks.value.length * selectedAudioLanguages.value.length,
);

const completeCopying = () => {
  isCopyingStep.value = false;
  selectedAudioLanguages.value = [];
  completedMigrations.value = [];
  bulkUpdateDialog.value = false;
};

return { __sfc: true,audioLanguagesTableHeaders, props, bulkUpdateDialog, isCopyingStep, selectedAudioLanguages, selectedDuplicateBooks, completedMigrations, handleSelectedDuplicateBooks, toggleDialog, getLanguageLabel, audioLanguages, getUpdateKey, setCopyState, copyLanguagesForBookByLanguage, copyLanguagesForBook, copyLanguages, handleSubmit, getCopyStatus, isCopyInProgress, isCopyDone, hasCopyError, areAllCopiesDone, completeCopying, DuplicateBooksTable }
}

})